/**
 * Tabela do Cadastro de Suprimentos (redesign 2026-09-15).
 *
 * Zero scroll lateral: `table-layout: fixed`, larguras por peso e "…" com
 * tooltip em toda célula. Duas vistas: Lista e Por tipo (cabeçalho de grupo
 * colapsável com família, tipo, contagem, abaixo do mínimo e valor em
 * estoque). Paginação no cliente (25/50/100) — a lista inteira já está na
 * memória. Clique na linha abre o detalhe; checkbox e ações não propagam.
 *
 * No celular (≤ 768px) a tabela vira lista de cards com as mesmas peças
 * (amostra, nome, selos, barra de saldo, último preço) — e o agrupamento
 * vira um cabeçalho de seção. Nada de tabela espremida.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiChevronDown, FiEdit2, FiMoreHorizontal } from "react-icons/fi";
import styled from "styled-components";

import Checkbox from "../../../components/Checkbox";
import { MenuItem, MenuList } from "../../../components/EntityHeader/styles.jsx";
import { formatarReaisBR, plural } from "../../../utils/dinheiro";
import { mq } from "../../../styles/canonical/breakpoints";
import { CheckCell, Mono, PageBtn, Table, TableFooter, TableScroll, TapCheck } from "../_shared/quadro/styles";
import { TruncCell } from "../styles";
import { labelTipo } from "./colunas";
import { BarraSaldo, CelulaUltimoPreco, SelosDoItem, Swatch } from "./celulas";
import { agruparPorTipo, especificacaoDe, infoSaldo, semFornecedor, ultimoPrecoDe } from "./derivados";

const OPCOES_POR_PAGINA = [25, 50, 100];
const PESO_SELECAO = 3;
const PESO_ACOES = 9;
// Amostra (textura ou cor) fixa no início da linha, qualquer que seja a
// escolha de colunas: o material se reconhece pelo padrão antes do nome.
const PESO_AMOSTRA = 5;

// ---------------------------------------------------------------- estilos

const Tabela = styled(Table)`
  th { height: 40px; }
  td { padding-top: 8px; padding-bottom: 8px; }
  tbody tr[data-abaixo="true"] td:first-child { box-shadow: inset 3px 0 0 var(--danger-solid); }
  tbody tr.grupo { cursor: pointer; user-select: none; }
  tbody tr.grupo td { height: 38px; padding-top: 0; padding-bottom: 0; background: var(--bg-elevated); border-bottom: 1px solid var(--border); }
  tbody tr.grupo:hover td { background: var(--surface-2); }
`;

const AmostraCell = styled.td`
  && { padding-left: 10px; padding-right: 0; }
  > span { display: flex; }
`;

const GrupoHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  > .chev { display: inline-flex; color: var(--ink-3); transition: transform .15s ease; transform: rotate(${({ $aberto }) => ($aberto ? 0 : -90)}deg); }
  > .fam { font-family: var(--font-mono); font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-3); }
  > .nome { font-weight: 700; font-size: 13px; }
  > .n { font-size: 12px; color: var(--ink-3); }
  > .alerta { font-size: 11.5px; font-weight: 600; color: var(--danger-text); background: var(--danger-bg); border: 1px solid var(--danger-border); border-radius: 999px; padding: 1px 8px; }
  > .total { margin-left: auto; font-size: 12px; color: var(--ink-3); white-space: nowrap; }
`;

const BtnLinha = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-3);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color .12s ease, border-color .12s ease;
  &:hover { color: var(--ink); border-color: var(--border-strong); }
  /* Alvo de toque (auditoria mobile 2026-09-24, S5): 28px era o menor botão
     da tela; 40px com 4px de folga entre editar e o menu. */
  @media ${mq.mobile} {
    width: 40px;
    height: 40px;
  }
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid ${({ $sel }) => ($sel ? "var(--accent)" : "var(--border)")};
  border-radius: 14px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: var(--ink);
  cursor: pointer;
  ${({ $sel }) => ($sel ? "background: var(--accent-soft);" : "")}
  > .topo { display: flex; align-items: center; gap: 10px; min-width: 0; }
  > .topo > .texto { flex: 1; min-width: 0; }
  > .topo > .texto > .nome { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-weight: 600; font-size: 14px; }
  > .topo > .texto > .sub { font-size: var(--text-min); color: var(--ink-3); margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .meio { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
`;

const SecaoCards = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 2px 2px;
  font-size: 12px;
  color: var(--ink-3);
  min-width: 0;
  > .fam { font-family: var(--font-mono); font-size: 11px; letter-spacing: .06em; text-transform: uppercase; flex: 0 0 auto; }
  > .nome { font-weight: 700; color: var(--ink); font-size: 13px; flex: 1 1 auto; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .n { flex: 0 0 auto; white-space: nowrap; }
  > .alerta { color: var(--danger-text); font-weight: 600; flex: 0 0 auto; white-space: nowrap; }
`;

// ---------------------------------------------------------------- menu

const ALTURA_ITEM_MENU = 34;
const MARGEM_MENU = 6;

/**
 * Onde desenhar o menu: preso ao botao, em posicao FIXA e fora da tabela.
 * Dentro da celula ele era cortado pelo `overflow: hidden` da tabela (regra
 * de zero rolagem lateral) e ficava escondido. Sem espaco embaixo, abre
 * para cima.
 */
export function posicaoDoMenu(botao, qtdItens, alturaJanela) {
  const altura = qtdItens * ALTURA_ITEM_MENU + 2 * MARGEM_MENU;
  const cabeEmbaixo = botao.bottom + MARGEM_MENU + altura <= alturaJanela;
  return {
    right: Math.max(8, window.innerWidth - botao.right),
    ...(cabeEmbaixo ? { top: botao.bottom + MARGEM_MENU } : { top: "auto", bottom: alturaJanela - botao.top + MARGEM_MENU }),
  };
}

function MenuAcoes({ produto, onEditar, onDuplicar, onMovimentar, onInativar, onApagar }) {
  const [posicao, setPosicao] = useState(null);
  const botaoRef = useRef(null);
  const aberto = posicao !== null;
  useEffect(() => {
    if (!aberto) return undefined;
    const fechar = () => setPosicao(null);
    const aoTeclar = (e) => { if (e.key === "Escape") fechar(); };
    // Rolar ou redimensionar deixaria o menu solto no lugar antigo: fecha.
    document.addEventListener("click", fechar);
    document.addEventListener("keydown", aoTeclar);
    window.addEventListener("scroll", fechar, true);
    window.addEventListener("resize", fechar);
    return () => {
      document.removeEventListener("click", fechar);
      document.removeEventListener("keydown", aoTeclar);
      window.removeEventListener("scroll", fechar, true);
      window.removeEventListener("resize", fechar);
    };
  }, [aberto]);
  const itens = [
    onDuplicar ? { label: "Duplicar", onClick: () => onDuplicar(produto) } : null,
    onMovimentar ? { label: "Movimentar", onClick: () => onMovimentar(produto) } : null,
    onInativar && produto.ativo ? { label: "Inativar", onClick: () => onInativar(produto) } : null,
    onApagar ? { label: "Apagar", danger: true, onClick: () => onApagar(produto) } : null,
  ].filter(Boolean);
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }} onClick={(e) => e.stopPropagation()}>
      {onEditar ? (
        <BtnLinha type="button" title="Editar" aria-label={`Editar ${produto.codigo}`} onClick={() => onEditar(produto)}>
          <FiEdit2 size={12} />
        </BtnLinha>
      ) : null}
      {itens.length ? (
        <>
          <BtnLinha
            ref={botaoRef} type="button" title="Mais ações" aria-label={`Ações de ${produto.codigo}`}
            aria-haspopup="menu" aria-expanded={aberto}
            onClick={() => setPosicao((p) => (p ? null : posicaoDoMenu(
              botaoRef.current.getBoundingClientRect(), itens.length, window.innerHeight,
            )))}
          >
            <FiMoreHorizontal size={14} />
          </BtnLinha>
          {aberto ? createPortal(
            <MenuList role="menu" aria-label={`Ações de ${produto.codigo}`} style={{ position: "fixed", zIndex: 1100, ...posicao }} onClick={(e) => e.stopPropagation()}>
              {itens.map((it) => (
                <MenuItem key={it.label} $danger={it.danger}>
                  <button type="button" role="menuitem" onClick={() => { setPosicao(null); it.onClick(); }}>{it.label}</button>
                </MenuItem>
              ))}
            </MenuList>,
            document.body,
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- linhas

/** Lista plana do que aparece: cabeçalhos de grupo e itens (fora de grupo fechado). */
function montarLinhas(produtos, vista, tipos, fechados) {
  if (vista !== "tipo") return produtos.map((p) => ({ tipo: "item", p }));
  const linhas = [];
  agruparPorTipo(produtos, tipos).forEach((g) => {
    const aberto = !fechados.has(g.key);
    linhas.push({ tipo: "grupo", g, aberto });
    if (aberto) g.itens.forEach((p) => linhas.push({ tipo: "item", p, g }));
  });
  return linhas;
}

export default function TabelaSuprimentos({
  produtos, colunas, ctx, vista, mobile,
  selecionados, onToggleUm, onToggleVarios, onAbrir,
  onEditar, onDuplicar, onMovimentar, onInativar, onApagar, podeSelecionar,
}) {
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(OPCOES_POR_PAGINA[0]);
  const [fechados, setFechados] = useState(() => new Set());

  const linhas = useMemo(() => montarLinhas(produtos, vista, ctx.tipos, fechados), [produtos, vista, ctx.tipos, fechados]);
  const total = linhas.filter((l) => l.tipo === "item").length;
  const paginas = Math.max(1, Math.ceil(linhas.length / porPagina));
  useEffect(() => { setPagina(1); }, [produtos, vista, porPagina]);
  useEffect(() => { if (pagina > paginas) setPagina(paginas); }, [pagina, paginas]);

  const visiveis = useMemo(() => {
    const fatia = linhas.slice((pagina - 1) * porPagina, pagina * porPagina);
    // Página que começa no meio de um grupo repete o cabeçalho do grupo.
    if (fatia.length && fatia[0].tipo === "item" && fatia[0].g) fatia.unshift({ tipo: "grupo", g: fatia[0].g, aberto: true, repetido: true });
    return fatia;
  }, [linhas, pagina, porPagina]);

  const idsPagina = visiveis.filter((l) => l.tipo === "item").map((l) => l.p.id);
  const todosMarcados = idsPagina.length > 0 && idsPagina.every((id) => selecionados.has(id));
  const algumMarcado = idsPagina.some((id) => selecionados.has(id));
  const alternarGrupo = (key) => setFechados((atual) => {
    const novo = new Set(atual);
    if (novo.has(key)) novo.delete(key); else novo.add(key);
    return novo;
  });

  const itensAntes = linhas.slice(0, (pagina - 1) * porPagina).filter((l) => l.tipo === "item").length;
  const ini = total ? itensAntes + 1 : 0;
  const fim = itensAntes + idsPagina.length;

  const rodape = (
    <TableFooter style={{ background: "var(--surface-2)", fontSize: 12.5 }}>
      <span><b style={{ color: "var(--ink-2)", fontWeight: 600 }}>{ini}–{fim}</b> de {total}</span>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        Por página
        <select
          value={porPagina}
          onChange={(e) => setPorPagina(Number(e.target.value))}
          aria-label="Itens por página"
          style={{ height: 26, borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--ink-2)", font: "inherit", fontSize: 12 }}
        >
          {OPCOES_POR_PAGINA.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <span className="spacer" />
      {paginas > 1 ? (
        <span style={{ display: "inline-flex", gap: 4 }}>
          <PageBtn type="button" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1} aria-label="Página anterior">‹</PageBtn>
          {Array.from({ length: paginas }, (_, i) => i + 1).slice(Math.max(0, pagina - 3), pagina + 2).map((p) => (
            <PageBtn
              key={p}
              type="button"
              $on={p === pagina}
              onClick={() => setPagina(p)}
              style={p === pagina ? { background: "var(--accent)", color: "var(--ink-on-accent)", borderColor: "var(--accent)", fontWeight: 700 } : undefined}
            >
              {p}
            </PageBtn>
          ))}
          <PageBtn type="button" onClick={() => setPagina((p) => p + 1)} disabled={pagina === paginas} aria-label="Próxima página">›</PageBtn>
        </span>
      ) : null}
    </TableFooter>
  );

  const acoes = (p) => (
    <MenuAcoes produto={p} onEditar={onEditar} onDuplicar={onDuplicar} onMovimentar={onMovimentar} onInativar={onInativar} onApagar={onApagar} />
  );

  // ------------------------------------------------------------ celular
  if (mobile) {
    return (
      <div>
        <Cards>
          {visiveis.map((l) => {
            if (l.tipo === "grupo") {
              return (
                <SecaoCards key={`g-${l.g.key}`} role="button" tabIndex={0} onClick={() => alternarGrupo(l.g.key)} onKeyDown={(e) => { if (e.key === "Enter") alternarGrupo(l.g.key); }}>
                  <FiChevronDown size={13} style={{ transform: l.aberto ? "none" : "rotate(-90deg)", transition: "transform .15s" }} />
                  <span className="fam">{l.g.familia}</span>
                  <span className="nome" title={l.g.label}>{l.g.label}</span>
                  <span className="n">{plural(l.g.itens.length, "item", "itens")}</span>
                  {l.g.abaixo ? <span className="alerta">{l.g.abaixo} abaixo do mín.</span> : null}
                </SecaoCards>
              );
            }
            const p = l.p;
            const info = infoSaldo(p);
            const sel = selecionados.has(p.id);
            return (
              <Card key={p.id} $sel={sel} data-abaixo={info.abaixo ? "true" : "false"} onClick={() => onAbrir(p.id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onAbrir(p.id); }}>
                <div className="topo">
                  {podeSelecionar ? (
                    <TapCheck onClick={(e) => { e.stopPropagation(); onToggleUm(p.id); }}>
                      <Checkbox checked={sel} readOnly aria-label={`Selecionar ${p.codigo}`} />
                    </TapCheck>
                  ) : null}
                  <Swatch produto={p} size={40} />
                  <div className="texto">
                    <div className="nome">
                      <span>{p.nome}</span>
                      <SelosDoItem produto={p} semFornecedor={semFornecedor(p)} />
                    </div>
                    <div className="sub"><Mono style={{ color: "var(--ink-2)" }}>{p.codigo}</Mono> · {labelTipo(p, ctx.tipos)} · {especificacaoDe(p, ctx.tipos)}</div>
                  </div>
                  {acoes(p)}
                </div>
                <div className="meio">
                  <BarraSaldo info={info} saldoNegativo={Number(p.saldo) < 0} />
                  <CelulaUltimoPreco ultimo={ultimoPrecoDe(p)} />
                </div>
              </Card>
            );
          })}
        </Cards>
        <TableScroll style={{ marginTop: 10 }}>{rodape}</TableScroll>
      </div>
    );
  }

  // ------------------------------------------------------------ desktop
  // Larguras em % sobre pesos (calc() em <col> não é honrado com table-layout
  // fixed): seleção e ações entram como pesos fixos pequenos.
  const pesoTotal = colunas.reduce((acc, c) => acc + c.largura, 0) + PESO_ACOES + PESO_AMOSTRA + (podeSelecionar ? PESO_SELECAO : 0);
  const largura = (peso) => `${((peso / pesoTotal) * 100).toFixed(2)}%`;
  const nCols = colunas.length + 2 + (podeSelecionar ? 1 : 0);

  return (
    <TableScroll style={{ borderRadius: 12 }}>
      <Tabela>
        <colgroup>
          {podeSelecionar ? <col style={{ width: largura(PESO_SELECAO) }} /> : null}
          <col style={{ width: largura(PESO_AMOSTRA) }} />
          {colunas.map((c) => <col key={c.key} style={{ width: largura(c.largura) }} />)}
          <col style={{ width: largura(PESO_ACOES) }} />
        </colgroup>
        <thead>
          <tr>
            {podeSelecionar ? (
              <CheckCell as="th" onClick={(e) => e.stopPropagation()} style={{ padding: "0 0 0 14px" }}>
                <Checkbox
                  aria-label={todosMarcados ? "Desmarcar todos desta página" : "Selecionar todos desta página"}
                  checked={todosMarcados}
                  ref={(el) => { if (el) el.indeterminate = !todosMarcados && algumMarcado; }}
                  onChange={() => onToggleVarios(idsPagina, !todosMarcados)}
                  disabled={idsPagina.length === 0}
                />
              </CheckCell>
            ) : null}
            <th aria-label="Amostra" />
            {colunas.map((c) => (
              <th key={c.key} style={c.alinhar === "right" ? { textAlign: "right" } : undefined} title={c.label}>
                <TruncCell>{c.label}</TruncCell>
              </th>
            ))}
            <th aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {visiveis.map((l) => {
            if (l.tipo === "grupo") {
              return (
                <tr key={`g-${l.g.key}${l.repetido ? "-r" : ""}`} className="grupo" onClick={() => alternarGrupo(l.g.key)} data-testid="grupo-tipo">
                  <td colSpan={nCols}>
                    <GrupoHead $aberto={l.aberto}>
                      <span className="chev"><FiChevronDown size={13} /></span>
                      <span className="fam">{l.g.familia}</span>
                      <span className="nome">{l.g.label}</span>
                      <span className="n">{plural(l.g.itens.length, "item", "itens")}</span>
                      {l.g.abaixo ? <span className="alerta">{l.g.abaixo} abaixo do mínimo</span> : null}
                      <span className="total">{formatarReaisBR(l.g.valor)} em estoque</span>
                    </GrupoHead>
                  </td>
                </tr>
              );
            }
            const p = l.p;
            const info = infoSaldo(p);
            const sel = selecionados.has(p.id);
            return (
              <tr
                key={p.id}
                onClick={() => onAbrir(p.id)}
                data-selecionada={sel ? "true" : "false"}
                data-abaixo={info.abaixo ? "true" : "false"}
              >
                {podeSelecionar ? (
                  <CheckCell onClick={(e) => { e.stopPropagation(); onToggleUm(p.id); }} style={{ padding: "0 0 0 14px" }}>
                    <Checkbox checked={sel} readOnly aria-label={`Selecionar ${p.codigo}`} />
                  </CheckCell>
                ) : null}
                <AmostraCell><Swatch produto={p} /></AmostraCell>
                {colunas.map((c) => {
                  const texto = c.texto(p, ctx);
                  return (
                    <td key={c.key} title={c.render ? undefined : texto} style={c.alinhar === "right" ? { textAlign: "right" } : undefined}>
                      {c.render ? c.render(p, ctx) : <TruncCell>{texto}</TruncCell>}
                    </td>
                  );
                })}
                <td>{acoes(p)}</td>
              </tr>
            );
          })}
        </tbody>
      </Tabela>
      {rodape}
    </TableScroll>
  );
}

